import React from 'react';
import { useLocation, useOutlet } from 'react-router-dom';
import { Sidebar } from './sidebar/sidebar';
import { LayoutContainer, ContentWrapper } from './layout-container.styled';
import {
  ContentTransition,
  directionBetween,
  segmentOf,
  useKeyedDirection,
} from '../route-transition/route-transition';
import type { BuildingData as Building } from '../../interfaces/building-interfaces';
import type { NavigateFunction } from 'react-router-dom';
import { ClockSurfaceProvider } from '../../context/clock-context';

/**
 * THE DESK SHELL — one rail, one building panel, one `<main>`, mounted ONCE
 * for every desk screen.
 *
 * Every desk screen used to build this tree itself, so going from the main
 * menu to the parcel shelf tore down the rail, the logo, the account menu and
 * the building panel and rebuilt identical ones — the thing that made the
 * product feel like a website loading another page. Now the shell is a
 * layout route: it stays put, and only the content inside `<main>` moves.
 *
 * The content moves with direction: leaving the menu slides in from the
 * right, returning slides in from the left, and the two log sections swap
 * vertically (see route-transition.tsx). `useOutlet()` rather than `<Outlet/>`
 * so the leaving screen keeps rendering its OWN route while it exits.
 *
 * IT IS ALSO A BUILDING-CLOCK SURFACE (R-T12): everything drawn inside it
 * formats in the building's zone, for whoever is at the desk — an owner or a
 * manager included. The provider below is how the shared components know.
 */

export interface DeskShellSidebarProps {
  navigate: NavigateFunction;
  selectedOption: string;
  path: string;
  selectedBuilding?: Building | null;
  buildings?: Building[];
  buildingsLoading?: boolean;
  onSelectBuilding?: (buildingId: string) => void;
}

interface DeskShellProps {
  sidebar: DeskShellSidebarProps;
}

const CONTENT_LAYER_STYLE: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
  minHeight: 0,
};

export const DeskShell: React.FC<DeskShellProps> = ({ sidebar }) => {
  const location = useLocation();
  const outlet = useOutlet();
  const segment = segmentOf(location.pathname);
  const direction = useKeyedDirection(segment, directionBetween);

  return (
    <ClockSurfaceProvider surface="desk">
    <LayoutContainer>
      <Sidebar {...sidebar} />
      <ContentWrapper>
        <ContentTransition
          contentKey={segment}
          direction={direction}
          style={CONTENT_LAYER_STYLE}
        >
          {outlet}
        </ContentTransition>
      </ContentWrapper>
    </LayoutContainer>
    </ClockSurfaceProvider>
  );
};
